import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { UtilsService } from 'src/app/services/utils/utils.service';
import {
  getAstroAdjusted,
  getAstroTime,
  getLumPeriodBadgeClass,
  getRelaySwitchPath,
  normalizeLumSchedulePeriodIds,
  normalizeWeekdayMask,
  timecfgToFormattedTime,
} from 'src/app/services/LUM/lum.helper';

const WEEKDAY_ALL = 254;
const WEEKDAY_WORK = 62;
const WEEKDAY_WEEKEND = 192;

const DAYS_OF_WEEK = [
  { value: 2,   short: 'L', key: 'monday' },
  { value: 4,   short: 'M', key: 'tuesday' },
  { value: 8,   short: 'X', key: 'wednesday' },
  { value: 16,  short: 'J', key: 'thursday' },
  { value: 32,  short: 'V', key: 'friday' },
  { value: 64,  short: 'S', key: 'saturday' },
  { value: 128, short: 'D', key: 'sunday' },
] as const;


const SELECTABLE_PERIODS = [0, 1, 2, 3, 4, 5] as const;

@Component({
  selector: 'app-modal-lum-schedule',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './modal-lum-schedule.component.html',
  styleUrl: './modal-lum-schedule.component.scss',
})
export class ModalLumScheduleComponent implements OnInit {

  @Input() schedule: any | null = null;
  @Input() astcalData: { stat?: Record<string, unknown> } | null = null;
  draft: any;

  readonly daysOfWeek = DAYS_OF_WEEK;
  readonly selectablePeriods = SELECTABLE_PERIODS;
  readonly getRelaySwitchPath = getRelaySwitchPath;
  readonly getPeriodSelectedButtonClass = getLumPeriodBadgeClass;
  readonly modeOptions = [
    { value: 'auto', icon: 'fas fa-calendar-week', labelKey: 'auto' },
    { value: 'cyclic', icon: 'fas fa-repeat', labelKey: 'cyclic' },
  ] as const;
  readonly startTypeOptions = [
    { value: 'fixed', icon: 'fas fa-clock', labelKey: 'fixedTime' },
    { value: 'orto', icon: 'fas fa-sun', labelKey: 'sunrise' },
    { value: 'ocaso', icon: 'fas fa-moon', labelKey: 'sunset' },
  ] as const;

  constructor(
    public activeModal: NgbActiveModal,
    private utils: UtilsService,
  ) {}

  ngOnInit(): void {
    this.draft = this.createEmptyLumScheduleDraft();
    if (this.schedule) {
      this.applyScheduleToDraft(this.schedule);
    }
  }

  /** Borrador vacío para una programación nueva. */
  private createEmptyLumScheduleDraft(): any {
    return {
      id: null,
      mode: 'auto',
      name: '',
      relayAction: 'on',
      start: { type: 'fixed', timeStr: '00:00', offsetSign: '+', offsetTime: '00:00' },
      end: { type: 'fixed', timeStr: '00:00', offsetSign: '+', offsetTime: '00:00' },
      duration_sec: 0,
      cycle_on_sec: 0,
      cycle_off_sec: 0,
      weekdayMask: WEEKDAY_ALL,
      enabled: true,
      period_ids: [],
    };
  }

  /** Rellena el borrador actual con los datos de una programación existente. */
  private applyScheduleToDraft(schedule: any): void {
    const cfg = schedule?.cfg ?? {};
    const timeCfg = cfg.time_cfg ?? {};
    const calendar = timeCfg.calendar ?? {};

    this.draft.mode = cfg.mode;
    this.draft.id = schedule.id;
    this.draft.name = String(cfg.name ?? '').trim();
    this.draft.enabled = cfg.enabled === true || Number(cfg.enabled) === 1;
    this.draft.relayAction = String(cfg.actions?.[0]?.start_action ?? 'on').toLowerCase() === 'off' ? 'off' : 'on';
    this.draft.start = timecfgToFormattedTime(timeCfg.start);
    this.draft.end = timecfgToFormattedTime(timeCfg.end);
    this.draft.cycle_on_sec = Number(timeCfg.cycle_on_sec) || 0;
    this.draft.cycle_off_sec = Number(timeCfg.cycle_off_sec) || 0;
    this.draft.weekdayMask = normalizeWeekdayMask(calendar.weekday);
    this.draft.period_ids = normalizeLumSchedulePeriodIds(calendar.period_ids);

  }

  private currentPeriodIds(): number[] {
    return normalizeLumSchedulePeriodIds(this.draft?.period_ids);
  }

  /** General = ningún periodo festivo (lista vacía). El id 0 es un periodo válido. */
  isGeneralPeriod(): boolean {
    return this.currentPeriodIds().length === 0;
  }

  isPeriodSelected(periodId: number): boolean {
    return this.currentPeriodIds().includes(Number(periodId));
  }

  setGeneralPeriod(): void {
    this.draft.period_ids = [];
  }

  togglePeriod(periodId: number): void {
    const id = Number(periodId);
    const ids = this.currentPeriodIds();

    if (ids.includes(id)) {
      this.draft.period_ids = ids.filter((item) => item !== id);
      return;
    }

    this.draft.period_ids = [...ids, id].sort((a, b) => a - b);
  }

  getAstroTimePreview(type: string): string {
    return getAstroTime(this.astcalData, type);
  }

  getAstroAdjustedTime(type: string, offsetTime: string, offsetSign?: string): string {
    return getAstroAdjusted(this.astcalData, type, offsetTime, offsetSign ?? this.draft.start.offsetSign);
  }

  setStartType(type: string): void {
    this.draft.start.type = type;
    if (this.draft.start.type === 'fixed') {
      this.draft.start.offsetSign = '+';
      this.draft.start.offsetTime = '00:00';
    }
  }

  setEndType(type: string): void {
    this.draft.end.type = type;
    if (this.draft.end.type === 'fixed') {
      this.draft.end.offsetSign = '+';
      this.draft.end.offsetTime = '00:00';
    }
  }


  isWeekdaySelected(dayValue: number): boolean {
    return Boolean(normalizeWeekdayMask(this.draft.weekdayMask) & dayValue);
  }

  toggleWeekday(dayValue: number): void {
    let mask = normalizeWeekdayMask(this.draft.weekdayMask);
    if (mask & dayValue) {
      mask &= ~dayValue;
    } else {
      mask |= dayValue;
    }
    this.draft.weekdayMask = mask;
  }

  isWeekdayPreset(preset: 'weekdays' | 'weekend' | 'allDays'): boolean {
    const mask = normalizeWeekdayMask(this.draft.weekdayMask);
    if (preset === 'weekdays') {
      return mask === WEEKDAY_WORK;
    }
    if (preset === 'weekend') {
      return mask === WEEKDAY_WEEKEND;
    }
    return mask === WEEKDAY_ALL;
  }

  setWeekdayPreset(preset: 'weekdays' | 'weekend' | 'allDays'): void {
    if (preset === 'weekdays') {
      this.draft.weekdayMask = WEEKDAY_WORK;
    } else if (preset === 'weekend') {
      this.draft.weekdayMask = WEEKDAY_WEEKEND;
    } else {
      this.draft.weekdayMask = WEEKDAY_ALL;
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    this.activeModal.close({
      scheduleId: this.draft.id,
      ...this.draft,
      period_ids: this.currentPeriodIds(),
    });
  }
}
